import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import styles from '../style.module.scss';
import { FlyoutType } from '../Utils/utils';
import { useTranslation } from 'react-i18next';
import {
  Loan,
  LoanStatusResponse,
  PoolProcessingStatusCode,
  PortfolioInsuranceType,
  RationaleOption
} from '../types';
import { ForceStatusPill } from '../ForceStatusPill';
import {
  Button,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  ComboBox,
  ComboboxOption,
  Divider,
  LargeFormField,
  RadioButton
} from '@cmhc-schl/hearth-ui';
import { Remark } from '../../../types';
import LoansToBeUpdated from 'Feature/V2/HbtMasterPool/shared/LoansUpdate';
import { LoanInventoryTableFields } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/HbtPortfolioLoansInventoryTable/types';
import { isRemarkOther } from 'Feature/V2/Shared_Portfolio/utils/remarkOther';

interface Option {
  value: string;
  label: React.ReactNode;
}

export interface MainComponentInterface {
  setApprovalStatus: React.Dispatch<React.SetStateAction<string | null>>;
  setShowStatusChangeModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAddSection: React.Dispatch<React.SetStateAction<boolean>>;
  showAddSection: boolean;
  setEligibleLoans: React.Dispatch<React.SetStateAction<Loan[]>>;
  headerText?: string;
  loansToBeUpdatedTitle?: string;
  radioButtonOptions?: [Option, Option];
  flyoutLoans: Loan[];
  onAddLoans?: Function;
  onNotify: (icon: string, message: string, desc: string) => void;
  onErrorLoans: (loans: Loan[], eligibleLoans?: Loan[]) => void;
  proceedWithEligibleLoans: number;
  setFlyoutLoans: React.Dispatch<React.SetStateAction<Loan[]>>;
  approvalStatus: string | null;
  statusRequiredError: boolean;
  setStatusRequiredError: (val: boolean) => void;
  setIsAddingLoans: React.Dispatch<React.SetStateAction<boolean>>;
  isAddingLoans: boolean;
  newLoansInput: string;
  setNewLoansInput: (val: string) => void;
  addLoanslargeFormFieldErrMsg: string;
  setAddLoansLargeFormFieldErrMsg: (val: string) => void;
  loansTypeToBeAdded?: string;
  textFieldPlaceholder?: string;
  textFieldButtonText?: string;
  addLoansDescription?: string;
  addLoansTextFieldError?: string;
  toasterHeader?: string;
  toasterDesc?: string;
  addLoansTextFieldErrorTwo?: string;
  typeOfFlyout?: FlyoutType;
  typeOfPortfolioInsurance?: PortfolioInsuranceType;
  pptCode: string | null;
  applicationID: string | null;
  loansData?: LoanInventoryTableFields[];
  isFromLoansInventory?: boolean;
  masterPoolCmhcAccountNumber?: string;
  requestUrl?: string;
  proceedWithEligibleLoansSignal: number;
  handleRemarkChange: (option: ComboboxOption) => void;
  rationaleRequired: boolean;
  setRationaleRequired: React.Dispatch<React.SetStateAction<boolean>>;
  rationaleOptions: RationaleOption[];
  remark: Remark;
  largeFormFieldRequired: boolean;
  largeFormFieldErrMsg: string;
  isSubmitting: boolean;
  handleLargeFormField: (value: string) => void;
  submitOverrideLoanStatus: () => Promise<void>;
  closeFlyout: () => void;
  largeFormFieldValue: string;
  loansUpdateLargeFormFieldButtonText?: string;
}

export const MainComponent = ({
  largeFormFieldValue,
  setApprovalStatus,
  setShowAddSection,
  showAddSection,
  headerText,
  setEligibleLoans,
  setShowStatusChangeModal,
  radioButtonOptions,
  loansToBeUpdatedTitle,
  onNotify,
  flyoutLoans,
  onErrorLoans,
  proceedWithEligibleLoans,
  setFlyoutLoans,
  approvalStatus,
  statusRequiredError,
  setStatusRequiredError,
  newLoansInput,
  setNewLoansInput,
  addLoanslargeFormFieldErrMsg,
  setAddLoansLargeFormFieldErrMsg,
  loansTypeToBeAdded,
  textFieldPlaceholder,
  textFieldButtonText,
  addLoansDescription,
  addLoansTextFieldError,
  toasterHeader,
  toasterDesc,
  addLoansTextFieldErrorTwo,
  typeOfFlyout,
  typeOfPortfolioInsurance,
  pptCode,
  applicationID,
  loansData,
  isFromLoansInventory,
  masterPoolCmhcAccountNumber,
  requestUrl,
  proceedWithEligibleLoansSignal,
  handleRemarkChange,
  rationaleRequired,
  setRationaleRequired,
  rationaleOptions,
  remark,
  largeFormFieldRequired,
  largeFormFieldErrMsg,
  isSubmitting,
  handleLargeFormField,
  submitOverrideLoanStatus,
  closeFlyout,
  setIsAddingLoans,
  isAddingLoans,
  loansUpdateLargeFormFieldButtonText
}: MainComponentInterface) => {
  let noOfSection = 1;
  const { t } = useTranslation('HbtMasterPool');
  const largeFormFieldMaxChar = 300;

  const onApprovalStatusChange = useCallback(
    (value?: string) => {
      if (approvalStatus) {
        setStatusRequiredError(false);
      }
      const safeValue = value ?? '';
      if (flyoutLoans.length > 0 && approvalStatus && approvalStatus !== safeValue) {
        setShowStatusChangeModal(true);
      }
      setApprovalStatus(safeValue);
    },
    [flyoutLoans, approvalStatus]
  );

  const getListNumer = () => {
    const current = noOfSection;
    noOfSection = noOfSection + 1;
    return current;
  };

  return (
    <>
      <Box className={`${styles.flyoutWrapper} ${styles.flyoutDesc}`}>
        <Typography className={styles.flyout} variant="h6">
          {headerText ?? t('Override-Loan-Status-desc')}
        </Typography>
      </Box>
      {typeOfFlyout !== 'updateRemark' && (
        <Box className={`${styles.flyoutWrapper} ${styles.radioButtonContainer}`}>
          <Typography className={styles.sectionTitle}>
            {getListNumer()}) {t('Status-Heading')}
          </Typography>

          <RadioButton
            key={approvalStatus ?? 'none'}
            label={t('New-Status')}
            defaultOption={approvalStatus ?? undefined}
            options={radioButtonOptions ?? []}
            onChange={(value) => onApprovalStatusChange(value)}
            disabled={isAddingLoans}
            errorMessage={
              statusRequiredError ? t('Force-Status-Radio-Button-Error-Msg') : undefined
            }
            required={statusRequiredError}
          />
        </Box>
      )}

      <Box className={`${styles.flyoutWrapper}`}>
        <Typography className={styles.sectionTitle}>
          {getListNumer()}) {loansToBeUpdatedTitle ?? t('Add-Loan-Heading')}
        </Typography>
        <LoansToBeUpdated
          onNotify={onNotify}
          setEligibleLoans={setEligibleLoans}
          setShowAddSection={setShowAddSection}
          showAddSection={showAddSection}
          flyoutLoans={flyoutLoans}
          onErrorLoans={onErrorLoans}
          setFlyoutLoans={setFlyoutLoans}
          proceedWithEligibleLoans={proceedWithEligibleLoansSignal}
          approvalStatus={approvalStatus}
          statusRequiredError={statusRequiredError}
          setStatusRequiredError={setStatusRequiredError}
          setIsAddingLoans={setIsAddingLoans}
          isAddingLoans={isAddingLoans}
          newLoansInput={newLoansInput}
          setNewLoansInput={setNewLoansInput}
          addLoanslargeFormFieldErrMsg={addLoanslargeFormFieldErrMsg}
          setAddLoansLargeFormFieldErrMsg={setAddLoansLargeFormFieldErrMsg}
          loansTypeToBeAdded={loansTypeToBeAdded}
          textFieldPlaceholder={textFieldPlaceholder}
          textFieldButtonText={textFieldButtonText}
          addLoansDescription={addLoansDescription}
          addLoansTextFieldError={addLoansTextFieldError}
          toasterHeader={toasterHeader}
          toasterDesc={toasterDesc}
          addLoansTextFieldErrorTwo={addLoansTextFieldErrorTwo}
          typeOfFlyout={typeOfFlyout}
          typeOfPortfolioInsurance={typeOfPortfolioInsurance}
          pptCode={pptCode}
          applicationID={applicationID}
          loansData={loansData}
          isFromLoansInventory={isFromLoansInventory}
          masterPoolCmhcAccountNumber={masterPoolCmhcAccountNumber}
          requestUrl={requestUrl}
          loansUpdateLargeFormFieldButtonText={loansUpdateLargeFormFieldButtonText}
        />
      </Box>

      <Box className={`${styles.flyoutWrapper} ${styles.remarks}`}>
        <Typography className={styles.sectionTitle}>
          {getListNumer()}){' '}
          {typeOfFlyout === 'updateRemark'
            ? t('Add-Remarks-Heading-For-Add-Remarks')
            : t('Add-Remarks-Heading')}
        </Typography>
        <Box className={styles.remarksDropDown}>
          <Typography className={styles.briefText}>{t('Add-Remarks-Desc')}</Typography>

          <ComboBox
            ariaText={{
              value: t('Add-Remarks-Required-Error-Msg')
            }}
            onChange={(e, v) => handleRemarkChange(v as ComboboxOption)}
            placeholderText={t('Add-Remarks-Dropdown-Placeholder')}
            errorMessage={t('Add-Remarks-Required-Error-Msg')}
            error={rationaleRequired}
            options={rationaleOptions}
          />
          <Typography variant="h6">{t('Add-Remarks-Dropdown-Required-Message')}</Typography>
        </Box>
        {isRemarkOther(remark) && (
          <Box>
            <LargeFormField
              id="large-form-field"
              placeholder=""
              value={largeFormFieldValue}
              errorMessage={largeFormFieldRequired ? largeFormFieldErrMsg : ''}
              status={largeFormFieldRequired ? 'error' : 'success'}
              maxLength={largeFormFieldMaxChar}
              onChange={(e) => handleLargeFormField(e.target.value)}
            />
          </Box>
        )}
      </Box>

      <Box className={styles.flyoutFooter}>
        <Stack direction="row" spacing={3}>
          <Box width="100%">
            <Button
              type="button"
              name={`btn`}
              onClick={() => closeFlyout()}
              className={styles.cancelButton}
              variant={ButtonVariant.SECONDARY}
              text={{
                value: t('Cancel')
              }}
              ariaText={{
                value: t('Cancel')
              }}
            />
          </Box>
          <Box width="100%">
            <Button
              type="submit"
              name={`btn`}
              onClick={submitOverrideLoanStatus}
              className={styles.updateStatusButton}
              variant={ButtonVariant.PRIMARY}
              loading={isSubmitting}
              text={{
                value: isSubmitting
                  ? t('Updating')
                  : typeOfFlyout === 'updateRemark'
                    ? t('Add-Remarks-Flyout-Heading')
                    : t('Update-Status')
              }}
              ariaText={{
                value: isSubmitting
                  ? t('Updating')
                  : typeOfFlyout === 'updateRemark'
                    ? t('Add-Remarks-Flyout-Heading')
                    : t('Update-Status')
              }}
            />
          </Box>
        </Stack>
      </Box>
    </>
  );
};
